import React, { useState } from 'react';
import {
    Button,
    Card,
    Form,
    Input,
    message,
    Space,
    Typography,
} from 'antd';
import { LockOutlined, SafetyCertificateOutlined, UserOutlined } from '@ant-design/icons';
import AuthAPI from '../api/auth';
import TotpAPI from '../api/totp';

const { Title, Text } = Typography;

export default function LoginForm({ onLogin }) {
    const [messageApi, contextHolder] = message.useMessage();
    const [challengeToken, setChallengeToken] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [useBackupCode, setUseBackupCode] = useState(false);
    const [codeForm] = Form.useForm();

    const onPasswordSubmit = (values) => {
        setSubmitting(true);
        AuthAPI.login(values,
            (res) => {
                setSubmitting(false);
                if (res && res.status === 'totp_required' && res.challenge_token) {
                    setChallengeToken(res.challenge_token);
                    setUseBackupCode(false);
                    codeForm.resetFields();
                    messageApi.open({ type: 'info', content: 'Enter your 6-digit code.' });
                    return;
                }
                messageApi.open({ type: 'success', content: 'Welcome!' });
                onLogin(res);
            },
            (err) => {
                setSubmitting(false);
                console.log(err);
                messageApi.open({ type: 'error', content: 'Invalid username or password' });
            }
        );
    };

    const onCodeSubmit = (values) => {
        setSubmitting(true);
        TotpAPI.verify(
            { challenge_token: challengeToken, code: values.code },
            (res) => {
                setSubmitting(false);
                messageApi.open({ type: 'success', content: 'Welcome!' });
                setChallengeToken(null);
                onLogin(res);
            },
            (err) => {
                setSubmitting(false);
                console.log(err);
                messageApi.open({ type: 'error', content: 'Invalid or expired code' });
            }
        );
    };

    return (
        <div className="login-hero">
            {contextHolder}
            <div className="login-hero__inner">
                {challengeToken ? (
                    <>
                        <Title level={2} className="login-hero__title">
                            Two-Factor Authentication
                        </Title>
                        <Text type="secondary" className="login-hero__subtitle">
                            Enter the 6-digit code from your authenticator app,<br />
                            or one of your backup codes.
                        </Text>
                        <Card>
                            <Form
                                form={codeForm}
                                layout="vertical"
                                onFinish={onCodeSubmit}
                                autoComplete="off"
                            >
                                <Form.Item
                                    name="code"
                                    rules={[{
                                        required: true,
                                        message: useBackupCode
                                            ? 'Enter a backup code'
                                            : 'Enter the 6-digit code',
                                    }]}
                                >
                                    {useBackupCode ? (
                                        <Input
                                            size="large"
                                            autoFocus
                                            prefix={<SafetyCertificateOutlined />}
                                            placeholder="Backup code"
                                            autoComplete="one-time-code"
                                        />
                                    ) : (
                                        <Input.OTP
                                            size="large"
                                            length={6}
                                            autoFocus
                                            inputMode="numeric"
                                            formatter={(s) => s.replace(/\D/g, '')}
                                        />
                                    )}
                                </Form.Item>
                                <Space
                                    style={{ width: '100%', justifyContent: 'space-between' }}
                                    wrap
                                >
                                    <Button
                                        type="link"
                                        onClick={() => {
                                            setUseBackupCode((v) => !v);
                                            codeForm.resetFields(['code']);
                                        }}
                                        disabled={submitting}
                                        style={{ paddingLeft: 0 }}
                                    >
                                        {useBackupCode
                                            ? 'Use authenticator code'
                                            : 'Use a backup code instead'}
                                    </Button>
                                    <Space>
                                        <Button onClick={() => setChallengeToken(null)} disabled={submitting}>
                                            Back
                                        </Button>
                                        <Button type="primary" htmlType="submit" loading={submitting}>
                                            Verify
                                        </Button>
                                    </Space>
                                </Space>
                            </Form>
                        </Card>
                    </>
                ) : (
                    <>
                        <Title level={2} className="login-hero__title">
                            Sign in to ChulaSSO
                        </Title>
                        <Text type="secondary" className="login-hero__subtitle">
                            Use your IT account (the same one for Chula email).
                        </Text>
                        <Card>
                            <Form
                                name="login"
                                layout="vertical"
                                onFinish={onPasswordSubmit}
                                autoComplete="on"
                            >
                                <Form.Item
                                    name="username"
                                    rules={[{ required: true, message: 'Please enter your username' }]}
                                >
                                    <Input
                                        size="large"
                                        prefix={<UserOutlined />}
                                        placeholder="Student ID or username"
                                        autoComplete="username"
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="password"
                                    rules={[{ required: true, message: 'Please enter your password' }]}
                                >
                                    <Input.Password
                                        size="large"
                                        prefix={<LockOutlined />}
                                        placeholder="Password"
                                        autoComplete="current-password"
                                    />
                                </Form.Item>
                                <Form.Item style={{ marginBottom: 0 }}>
                                    <Button
                                        block
                                        size="large"
                                        type="primary"
                                        htmlType="submit"
                                        loading={submitting}
                                    >
                                        Sign in
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}
